// PropertyCardSimplified.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  Call,
  LocationOn,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import axios from 'axios';
import buildingImage from './../../../../Images/house.jpeg';
import { AuthContext } from './../../../AuthContext/AuthContext';
import { BASE_URL } from './../../../Api/ApiUrls';
import CustomSearchBar from './../../Lease Module/CustomSearch';
import BottomNavbar from './../../Post Module/FormsBottomNavbar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

const API_BASE_URL = 'https://landnest.net:7000/api';

const PropertyCardSimplified = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useContext(AuthContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [properties, setProperties] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [showNoResults, setShowNoResults] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState([]);

  // Tab state - get from URL path
  const [currentTab, setCurrentTab] = useState(() => {
    const path = location.pathname;
    if (path.includes('/user-buy-simplified')) {
      return 1;
    }
    return 0;
  });

  const handleTabChange = (newValue) => {
    setCurrentTab(newValue);
    // Navigate to appropriate path
    if (newValue === 0) {
      navigate('/user-buy');
    } else {
      navigate('/user-buy-simplified');
    }
  };

  // Update currentTab when location changes
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/user-buy-simplified')) {
      setCurrentTab(1);
    } else if (path.includes('/user-buy')) {
      setCurrentTab(0);
    }
  }, [location.pathname]);

  const getPropertyImages = async (propertyId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/properties/${propertyId}/images`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch images');
      return data;
    } catch (error) {
      console.error('API Error fetching images:', error);
      return { success: false, images: [] };
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setShowNoResults(false);
      
      const categoriesResponse = await axios.get(`${BASE_URL}/property-category/`);
      setCategories(categoriesResponse.data);

      const propertiesResponse = await axios.get(`${API_BASE_URL}/api/properties/user/${userId}?type=sell`);
      
      const userProperties = propertiesResponse.data.data || propertiesResponse.data;
      const sortedProperties = userProperties.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );

      const parsed = await Promise.all(sortedProperties.map(async (item) => {
        const parseCoord = (coord) => {
          if (!coord) return null;
          const cleaned = String(coord).replace(/"/g, '').replace(':', '.');
          return parseFloat(cleaned);
        };

        const matchedCategory = categoriesResponse.data.find(
          cat => cat.category_id == item.category_id_id
        );

        const categoryName = matchedCategory ? matchedCategory.category : 'Property';

        let images = [buildingImage];
        try {
          const imageResponse = await getPropertyImages(item.property_id);
          if (imageResponse.success && imageResponse.images && imageResponse.images.length > 0) {
            images = imageResponse.images.map(img => {
              if (img.startsWith('http')) {
                return img;
              } else if (img.startsWith('/')) {
                return `${BASE_URL}${img}`;
              } else {
                return `${BASE_URL}/${img}`;
              }
            });
          }
        } catch (imgError) {
          console.error(`Error fetching images for property ${item.property_id}:`, imgError);
        }

        return {
          id: item.property_id,
          title: categoryName,
          location: item.location || 'Not specified',
          price: item.price,
          formattedPrice: `₹${Math.round(item.price).toLocaleString('en-IN')}`,
          date: item.created_at?.split('T')[0],
          facing: item.facing?.replace(/"/g, '') || 'N/A',
          area: item.site_area
            ? `${item.site_area} ${
                ["ft", "m", "cm", "in", "Sq.Yd"].includes(item.units)
                  ? `Sq ${item.units}`
                  : item.units || "Sq ft"
              }`
            : 'N/A',
          max_acres: item.max_acres ? `${item.max_acres} ${item.units || "acres"}` : 'N/A',
          posted_by: item.posted_by?.replace(/"/g, '') || 'Agent',
          lat: parseCoord(item.lat),
          long: parseCoord(item.long),
          mobile_no: item.mobile_no,
          property_name: item.property_name,
          images: images,
          propertyData: item,
          admin_status: item.Admin_status || '',
        };
      }));

      setProperties(parsed);
      
      const initialIndexes = {};
      parsed.forEach(property => {
        initialIndexes[property.id] = 0;
      });
      setCurrentImageIndex(initialIndexes);
      
      if (parsed.length === 0) {
        setShowNoResults(true);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  const handleNextImage = (propertyId, e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => {
      const currentIndex = prev[propertyId];
      const property = properties.find(p => p.id == propertyId);
      const nextIndex = (currentIndex + 1) % property.images.length;
      return {
        ...prev,
        [propertyId]: nextIndex
      };
    });
  };

  const handlePrevImage = (propertyId, e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => {
      const currentIndex = prev[propertyId];
      const property = properties.find(p => p.id == propertyId);
      const prevIndex = (currentIndex - 1 + property.images.length) % property.images.length;
      return {
        ...prev,
        [propertyId]: prevIndex
      };
    });
  };

  const openGoogleMapsWithDirections = (destLat, destLng) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const currentLat = position.coords.latitude;
          const currentLng = position.coords.longitude;
          const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${currentLat},${currentLng}&destination=${destLat},${destLng}&travelmode=driving`;
          window.open(googleMapsUrl, '_blank');
        },
        (error) => {
          console.error("Error getting location:", error);
          alert("Could not get your location. Please allow location access.");
        }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };

  const handleCallClick = async (e, property) => {
    e.stopPropagation();
    try {
      if (property.mobile_no) {
        await navigator.clipboard.writeText(property.mobile_no);
        alert(`Copied: ${property.mobile_no}`);
      } else {
        alert("Mobile number not available");
      }
    } catch (error) {
      alert("Failed to copy mobile number");
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const handleCheckboxChange = (propertyId) => {
    setSelectedProperties(prev => {
      if (prev.includes(propertyId)) {
        return prev.filter(id => id !== propertyId);
      } else {
        return [...prev, propertyId];
      }
    });
  };

  const filteredProperties = properties.filter((property) =>
    property.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    property.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Render simplified property card with checkbox
  const renderSimplifiedPropertyCard = (property) => (
    <Card
      key={property.id}
      sx={{
        mb: 1.2,
        mx: 2,
        borderRadius: 3,
        boxShadow: 2,
        position: 'relative',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': { transform: 'scale(1.015)', boxShadow: 4 },
      }}
      onClick={(e) => {
        const isInteractiveElement =
          e.target.closest('button') ||
          e.target.closest('svg') ||
          e.target.closest('input[type="checkbox"]');
        if (!isInteractiveElement) {
          navigate('/buy-description', { state: { propertyId: property.id, property: property.propertyData } });
        }
      }}
    >
      {/* Checkbox at top right */}
      <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }}>
        <input
          type="checkbox"
          checked={selectedProperties.includes(property.id)}
          onChange={(e) => {
            e.stopPropagation();
            handleCheckboxChange(property.id);
          }}
          style={{
            width: 20,
            height: 20,
            cursor: 'pointer',
            accentColor: '#1976d2',
          }}
        />
      </Box>

      <Box position="relative">
        <CardMedia
          component="img"
          image={property.images[currentImageIndex[property.id]]}
          alt="Property"
          sx={{
            width: '100%',
            height: '140px',
            objectFit: 'cover',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
        />

        {property.images.length > 1 && (
          <>
            <IconButton
              sx={{
                position: 'absolute',
                top: '50%',
                left: 8,
                opacity: 0,
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255,255,255,0.3)',
                backdropFilter: 'blur(2px)',
                '&:hover': {
                  opacity: 1,
                  backgroundColor: 'rgba(255,255,255,0.5)'
                }
              }}
              onClick={(e) => handlePrevImage(property.id, e)}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <IconButton
              sx={{
                position: 'absolute',
                top: '50%',
                right: 8,
                opacity: 0,
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255,255,255,0.3)',
                backdropFilter: 'blur(2px)',
                '&:hover': {
                  opacity: 1,
                  backgroundColor: 'rgba(255,255,255,0.5)'
                }
              }}
              onClick={(e) => handleNextImage(property.id, e)}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </>
        )}

        {property.images.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1
            }}
          >
            {property.images.map((_, index) => (
              <Box
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex(prev => ({
                    ...prev,
                    [property.id]: index
                  }));
                }}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: currentImageIndex[property.id] == index ? '#1976d2' : '#ccc',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s'
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      <CardContent sx={{ px: 2, py: 0.2, pb: '7px !important' }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            noWrap
            sx={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              pr: 1
            }}
          >
            {property.title}-{truncateText(property.property_name, 17)}
          </Typography>
          <Typography variant="body2" fontWeight="bold" color="primary" noWrap>
            ₹{Number(String(property.price).replace(/[^0-9]/g, '')).toLocaleString('en-IN')}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              pr: 1
            }}
          >
            {property.location}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {new Date(property.date).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" mt={0.2}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (property.lat && property.long) {
                openGoogleMapsWithDirections(property.lat, property.long);
              }
            }}
          >
            <LocationOn fontSize="small" color="action" />
          </IconButton>

          <Typography variant="caption" color="text.primary" ml={0.5}>
            Location
          </Typography>
          
          {(() => {
            const status = !property.admin_status || property.admin_status === '' ? 'pending' : property.admin_status.toLowerCase();
            
            const getStyles = () => {
              switch(status) {
                case 'approved': return { borderColor: '#5dc460f2', icon: <CheckCircleIcon sx={{ fontSize: 14, color: '#4caf50' }} />, label: 'approved' };
                case 'rejected': return { borderColor: '#f85347f2', icon: <CancelIcon sx={{ fontSize: 14, color: '#f44336' }} />, label: 'rejected' };
                default: return { borderColor: '#ff9800', icon: <HourglassEmptyIcon sx={{ fontSize: 14, color: '#ff9800' }} />, label: 'pending' };
              }
            };
            
            const { borderColor, icon, label } = getStyles();
            
            return (
              <Box sx={{ mx: 2, flex: 1, display: 'flex', justifyContent: 'center' }}>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: '4px', border: `1px solid ${borderColor}`, color: '#000000be', px: 1.5, py: 0.4, borderRadius: 0, fontSize: '0.7rem', fontWeight: 'bold', letterSpacing: '0.5px', backgroundColor: 'transparent' }}>
                  {icon}
                  {label}
                </Typography>
              </Box>
            );
          })()}
          
          <Box sx={{ flexGrow: 1 }} /> 
          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={<Call />}
            sx={{ textTransform: 'none', px: 1.2, py: 0.3, fontSize: '0.7rem' }}
            onClick={(e) => handleCallClick(e, property)}
          >
            Call
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {[
            { label: 'Facing', value: property.facing },
            { label: `Area `, value: property.area || property.max_acres },
            { label: 'Posted By', value: property.posted_by },
          ].map((item, index) => (
            <Box
              key={index}
              sx={{
                flex: 1,
                px: 1,
                py: 0.2,
                textAlign: 'center',
                borderRight: index < 2 ? '1px solid #e0e0e0' : 'none',
              }}
            >
              <Typography variant="caption" color="text.secondary" noWrap>
                {item.label}
              </Typography>
              <Typography variant="body2" fontWeight="bold" noWrap>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: 'rgb(239, 231, 221)',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <CircularProgress size={40} thickness={5} sx={{ color: '#000000' }} />
        <Typography variant="h7" sx={{ color: '#000000', fontWeight: 600 }}>
          Loading properties...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: 'rgb(239, 231, 221)', minHeight: '120vh' }}>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          backgroundColor: 'rgb(239, 231, 221)',
          px: 1,
          py: 1,
          boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
        }}
      >
        <CustomSearchBar
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Box
          sx={{
            mt: 1,
            px: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            My Properties
          </Typography>
        </Box>
      </Box>

      <Box sx={{ pt: { xs: '140px', sm: '150px' }, pb: 8 }}>
        {/* Button-style Tabs */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          mx: 2, 
          mb: 2,
          backgroundColor: '#e8e0d8',
          borderRadius: 2,
          padding: '4px',
        }}>
          <Button
            onClick={() => handleTabChange(0)}
            sx={{
              flex: 1,
              backgroundColor: currentTab === 0 ? '#1976d2' : 'transparent',
              color: currentTab === 0 ? 'white' : '#333',
              fontWeight: 'bold',
              fontSize: '0.85rem',
              textTransform: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              '&:hover': {
                backgroundColor: currentTab === 0 ? '#1565c0' : '#d5cdc5',
              },
              transition: 'all 0.3s ease',
            }}
          >
            All Properties
          </Button>
          <Button
            onClick={() => handleTabChange(1)}
            sx={{
              flex: 1,
              backgroundColor: currentTab === 1 ? '#1976d2' : 'transparent',
              color: currentTab === 1 ? 'white' : '#333',
              fontWeight: 'bold',
              fontSize: '0.85rem',
              textTransform: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              '&:hover': {
                backgroundColor: currentTab === 1 ? '#1565c0' : '#d5cdc5',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Selected Properties
          </Button>
        </Box>

        {/* Property Cards */}
        {filteredProperties.length > 0 ? (
          filteredProperties.map((property) => renderSimplifiedPropertyCard(property))
        ) : (
          showNoResults && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              px: 2
            }}>
              <Typography>
                {searchQuery ? 'No properties found matching your search.' : 'No properties found.'}
              </Typography>
            </Box>
          )
        )}
      </Box>
      <BottomNavbar />
    </Box>
  );
};

export default PropertyCardSimplified;